import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Wind, CloudRain, Loader2, AlertTriangle, Check, CloudOff } from "lucide-react";

const MAX_SAFE_WIND = 20; // knots
const _cache = {};

// YYYY-MM-DD in Australia/Brisbane
const bneDay = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-CA", { timeZone: "Australia/Brisbane" }); }
  catch { return ""; }
};

async function fetchWeather(location) {
  const key = location.trim().toLowerCase();
  if (_cache[key]) return _cache[key];
  const p = api.get("/weather", { params: { q: location } }).then((r) => r.data).catch(() => null);
  _cache[key] = p;
  return p;
}

export default function JobWeather({ location, dateISO }) {
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    let active = true;
    if (!location) { setState({ loading: false, data: null }); return; }
    setState({ loading: true, data: null });
    fetchWeather(location).then((w) => { if (active) setState({ loading: false, data: w }); });
    return () => { active = false; };
  }, [location, dateISO]);

  if (!location) return null;
  if (state.loading) {
    return <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-2"><Loader2 className="h-3 w-3 animate-spin" /> checking weather…</div>;
  }
  const w = state.data;
  if (!w) {
    return (
      <div data-testid="job-weather" className="mt-2 flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs text-slate-500">
        <CloudOff className="h-3.5 w-3.5 shrink-0" /> Weather unavailable for this location
      </div>
    );
  }

  const jobDay = bneDay(dateISO);
  const daily = (w.daily || []).find((d) => bneDay(d.date) === jobDay);
  const hoursThatDay = (w.hourly || []).filter((h) => bneDay(h.time) === jobDay);
  const maxWind = hoursThatDay.length ? Math.max(...hoursThatDay.map((h) => h.wind?.speed_knot ?? 0)) : null;

  if (!daily && hoursThatDay.length === 0) {
    return (
      <div data-testid="job-weather" className="mt-2 flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs text-slate-500">
        <CloudOff className="h-3.5 w-3.5 shrink-0" /> Outside 7-day BOM forecast
      </div>
    );
  }

  const temp = daily?.temp_max;
  const rain = daily?.rain?.chance;
  const flyable = maxWind == null ? null : maxWind <= MAX_SAFE_WIND;

  const tone = flyable === null ? "bg-slate-50 border-slate-200 text-slate-500"
    : flyable ? "bg-lime-50 border-lime-200 text-lime-800"
    : "bg-red-50 border-red-200 text-red-700";

  return (
    <div data-testid="job-weather" className={`mt-2 flex items-center flex-wrap gap-x-3 gap-y-1 rounded-lg border px-2.5 py-1.5 text-xs ${tone}`}>
      {flyable === null ? <CloudOff className="h-3.5 w-3.5" /> : flyable ? <Check className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
      <span className="font-semibold">
        {flyable === null ? "Beyond wind forecast" : flyable ? "Flyable" : "Wind caution"}
      </span>
      {temp != null && <span>{temp}°C</span>}
      {rain != null && <span className="inline-flex items-center gap-1"><CloudRain className="h-3 w-3" /> {rain}%</span>}
      {maxWind != null && <span className="inline-flex items-center gap-1"><Wind className="h-3 w-3" /> {maxWind} kn</span>}
      {daily?.short_text && <span className="text-slate-500 truncate max-w-[140px]">· {daily.short_text}</span>}
    </div>
  );
}
